import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import UsernameValidatedComponent from "../../components/UsernameValidatedComponent";
import { DoesEmailExist, DoesUserNameExist, post_User } from "../../services/Users";
import EmailValidatedToCheckExistComponent from "../../components/EmailValidatedToCheckExistComponent";
import PasswordValidation from "../../components/PasswordValidation";
import { MdErrorOutline } from "react-icons/md";
import Loading from "../../icons/Loading";
import { BiCheckCircle } from "react-icons/bi";
import { onChangePhoneNumber } from "../../utils/phoneNumberRegex";

const roles = [
    {
        id: 5,
        name: "Learner"
    },
    {
        id: 3,
        name: "Teacher"
    },
    {
        id: 4,
        name: "Parent"
    },
    {
        id: 2,
        name: "Contributor"
    }
];

export const SignUp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { emailForAccount } = location.state || {};
    const [selectedRoleId, setSelectedRoleId] = useState(roles[0]?.id);
    const [usernameTouched, setUsernameTouched] = useState<boolean>(false);
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [showConfirmPasswordError, setShowConfirmPasswordError] = useState<boolean>(false);
    const [passwordTextType, setPasswordTextType] = useState<boolean>(false);
    const [userData, setUserData] = useState<any>({
        firstName: "",
        lastName: "",
        userName: "",
        emailAddress: "",
        mobilPhoneNumber: "",
        password: "",
        forcePasswordChange: true,
        isActive: true,
        dateFormat: "MM/dd/yyyy",
        language: "English",
        bio: "",
        profilePic: "",
        roleId: roles[0]?.id,
        lastLogin: "",
        createdAt: new Date(),
        createdBy: "Website",
        updatedAt: "",
        updatedBy: ""
    });
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [successFullRegister, setSuccessFullRegister] = useState<boolean>(false);

    useEffect(() => {
        if (emailForAccount) {
            setUserData({
                ...userData,
                emailAddress: emailForAccount
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailForAccount])

    useEffect(() => {
        if (confirmPassword?.trim()) {
            setShowConfirmPasswordError(confirmPassword?.trim() !== userData?.password?.trim());
        }
    }, [confirmPassword, userData?.password])

    const togglePasswordVisibility = () => {
        setPasswordTextType(!passwordTextType);
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (showConfirmPasswordError) {
            return;
        }

        if (isProcessing) {
            return;
        }
        setIsProcessing(true);

        post_User({
            ...userData,
            roleId: selectedRoleId,
            isActive: selectedRoleId === 2 ? false : true,
            createdAt: new Date()
        })
            .then(() => {
                setIsProcessing(false);
                setSuccessFullRegister(true);
            })
            .catch(error => {
                setIsProcessing(false);
                console.log(error);
            });
    }

    return (
        <div className="flex">
            <div className="hidden md:flex w-1/2 px-8">
                <img
                    src="/images/loginImage.jpg"
                    alt="Students"
                    className="w-full"
                />
            </div>

            <div className="flex w-full md:w-1/2 items-start justify-center bg-[#f6f9fc]">
                <div className="w-full px-2 py-4">
                    <div className="flex items-center mb-6 border-b border-[#D0D0D0]">
                        <span className="font-semibold text-lg">Sunday School</span>
                    </div>

                    <div className="px-20 py-8 max-sm:px-4">
                        <h2 className="text-2xl font-semibold mb-6">Create an account</h2>

                        <div className="flex space-x-3 max-sm:flex-col max-sm:space-y-3 max-sm:space-x-0 my-8 w-full">
                            {
                                roles?.map((role, index: number) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedRoleId(role?.id)}
                                        className={`flex items-center md:space-x-3 max-sm:space-x-0 max-sm:justify-between p-2 rounded-md border max-sm:w-full
                                            ${selectedRoleId === role?.id
                                                ? "border-primary bg-[#f6f9fc] text-primary"
                                                : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                                            }`
                                        }
                                    >
                                        <span className="text-sm">{role?.name}</span>
                                        <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedRoleId === role?.id ? "border-primary" : "border-gray-400"}`}>
                                            {
                                                selectedRoleId === role?.id && (
                                                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                                                )
                                            }
                                        </span>
                                    </button>
                                ))
                            }
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold mb-1">First Name*</label>
                                    <input
                                        type="text"
                                        placeholder="First Name"
                                        className="w-full text-gray-600 text-sm bg-secondary border border-[#D0D0D0] rounded-md px-3 py-2 focus:ring-2 focus:ring-primary outline-none"
                                        value={userData?.firstName}
                                        onChange={(e) => setUserData({ ...userData, firstName: e.target.value?.trim() })}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold mb-1">Last Name*</label>
                                    <input
                                        type="text"
                                        placeholder="Last Name"
                                        className="w-full text-gray-600 text-sm bg-secondary border border-[#D0D0D0] rounded-md px-3 py-2 focus:ring-2 focus:ring-primary outline-none"
                                        value={userData?.lastName}
                                        onChange={(e) => setUserData({ ...userData, lastName: e.target.value?.trim() })}
                                        required
                                    />
                                </div>

                                <UsernameValidatedComponent
                                    userName={userData?.userName}
                                    setUserName={(value) => setUserData({ ...userData, userName: value?.trim() })}
                                    getUsername={DoesUserNameExist}
                                    className={{
                                        parentDiv: "",
                                        label: "block text-xs font-semibold mb-1",
                                        input: "w-full text-gray-600 text-sm bg-secondary border border-[#D0D0D0] rounded-md px-3 py-2 focus:ring-2 focus:ring-primary outline-none"
                                    }}
                                    touched={usernameTouched}
                                    setTouched={setUsernameTouched}
                                />

                                <EmailValidatedToCheckExistComponent
                                    email={userData?.emailAddress}
                                    setEmail={(value) => setUserData({ ...userData, emailAddress: value?.trim() })}
                                    getEmail={DoesEmailExist}
                                    className={{
                                        parentDiv: "",
                                        label: "block text-xs font-semibold mb-1",
                                        input: "w-full text-gray-600 text-sm bg-secondary border border-[#D0D0D0] rounded-md px-3 py-2 focus:ring-2 focus:ring-primary outline-none"
                                    }}
                                    touched={emailTouched}
                                    setTouched={setEmailTouched}
                                />

                                <div>
                                    <label className="block text-xs font-semibold mb-1">Phone Number*</label>
                                    <input
                                        type='text'
                                        placeholder='Phone No.'
                                        className="w-full text-gray-600 text-sm bg-secondary border border-[#D0D0D0] rounded-md px-3 py-2 focus:ring-2 focus:ring-primary outline-none"
                                        pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                                        value={userData?.mobilPhoneNumber}
                                        onChange={(e) => {
                                            setUserData({ ...userData, mobilPhoneNumber: onChangePhoneNumber(e, e.target.value) })
                                        }}
                                    />
                                </div>

                                <PasswordValidation
                                    password={userData?.password}
                                    setPassword={(value) => setUserData({ ...userData, password: value?.trim() })}
                                    isValid={isPasswordValid}
                                    setIsValid={setIsPasswordValid}
                                    className={{
                                        parentDiv: "relative",
                                        label: "block text-xs font-semibold mb-1",
                                        input: "w-full text-gray-600 text-sm bg-secondary border border-[#D0D0D0] rounded-md px-3 py-2 focus:ring-2 focus:ring-primary outline-none"
                                    }}
                                    touched={passwordTouched}
                                    setTouched={setPasswordTouched}
                                />

                                <div className="relative">
                                    <label className="block text-xs font-semibold mb-1">Confirm Password*</label>
                                    <div className="flex items-center">
                                        <input
                                            type={passwordTextType ? 'text' : 'password'}
                                            placeholder="Confirm Password"
                                            className="w-full text-gray-600 text-sm bg-secondary border border-[#D0D0D0] rounded-md px-3 py-2 focus:ring-2 focus:ring-primary outline-none pr-10"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                        />

                                        {
                                            passwordTextType ? <span className="material-symbols-outlined text-gray-500 absolute right-3 cursor-pointer text-lg" onClick={togglePasswordVisibility}>
                                                visibility_off
                                            </span> : <span className="material-symbols-outlined text-gray-500 absolute right-3 cursor-pointer text-lg" onClick={togglePasswordVisibility}>
                                                visibility
                                            </span>
                                        }
                                    </div>

                                    {showConfirmPasswordError && (
                                        <div className="flex items-center mt-2 text-xs text-red-500">
                                            <MdErrorOutline className="mr-1" style={{ fontSize: 16 }} />
                                            <span>
                                                Passwords do not match
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {
                                successFullRegister
                                    ? <div className="bg-green-50 border border-green-200 text-green-800 p-6 rounded-md text-center shadow-sm animate-fade-in mt-8">
                                        <div className="flex flex-col items-center justify-center gap-3">
                                            <BiCheckCircle className="h-12 w-12 text-green-500" />
                                            <h2 className="text-xl font-semibold">Registration Successful</h2>
                                            <p className="text-sm text-green-700">
                                                Your account has been created successfully.
                                            </p>
                                            <button
                                                className="mt-4 px-5 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
                                                onClick={() => navigate("/login")}
                                            >
                                                Go to Login
                                            </button>
                                        </div>
                                    </div>
                                    : <button
                                        type="submit"
                                        className={`w-full bg-primary text-white py-2 rounded-[6px] transition mt-10 font-medium ${isProcessing ? "cursor-default" : "cursor-pointer hover:bg-sky-600"}`}
                                    >
                                        {
                                            isProcessing
                                                ? <>
                                                    <Loading />
                                                    Processing...
                                                </>
                                                : "Continue"
                                        }
                                    </button>
                            }
                        </form>

                        {/* Footer Links */}
                        {
                            !successFullRegister && (
                                <p className="mt-6 text-sm text-gray-600 text-center">
                                    <span>Already have an account?{" "}</span>
                                    <NavLink
                                        to="/login"
                                        className="text-primary hover:underline"
                                    >
                                        Login
                                    </NavLink>
                                </p>
                            )
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}